import { z } from "zod";
import { amountToMinor } from "./documentPricing";

/**
 * Danh mục khiếu nại hàng hoá — MỘT danh mục cho cả hai chiều.
 *
 * Nguồn: hai file khách gửi 17/09/2026 — "After Service Terms for ERP" (25 loại
 * sự cố × mức độ × hình thức xử lý) và "ClaimType 索偿类型" (22 hình thức chuẩn).
 * Louis trả lời: khiếu nại của khách hàng, và khi nguyên nhân là vật liệu của nhà
 * cung cấp thì Hisense phạt nhà cung cấp — hai hồ sơ phải nhất quán. Nên cùng một
 * danh sách lỗi dùng cho khiếu nại khách (`customer`) và khiếu nại nhà cung cấp
 * (`supplier`), theo cách SAP QM tách thông báo Q1 (khách) / Q2 (nhà cung cấp) mà
 * vẫn dùng chung catalog mã lỗi.
 *
 * Chỗ CỐ Ý khác file khách (ghi ở docs/PROJECT_DECISIONS.md 17/09/2026):
 *  - File 1 dùng 36 cách ghi, 15 cách không có trong danh sách 22 mục. Cách ghi
 *    chỉ khác chữ được gộp về mã chuẩn ("Full refund" = Request full refund…).
 *    8 hình thức thật sự thiếu (downtime, repack, rework, relabel, revised
 *    document, reissue document/certificate, case by case) được THÊM vào danh
 *    sách chuẩn thay vì bỏ — bỏ thì 8 loại lỗi mất cách xử lý khách đã ghi.
 *  - "Ghi nhận, không đòi" (RECORD_ONLY) chọn được với MỌI loại lỗi: không đòi gì
 *    luôn là một kết cục hợp lệ.
 *  - Ba hình thức chỉ có nghĩa khi Hisense là bên đòi nhà cung cấp — cảnh cáo, tạm
 *    ngưng đặt hàng, đòi lại tiền khách phạt — không hiện ở khiếu nại của khách.
 *
 * Nhãn hiển thị (vi/en/zh-Hans) ở apps/web/src/i18n/claims.ts; ở đây chỉ có mã,
 * cấu trúc và quy tắc, để API và giao diện kiểm cùng một bộ luật.
 */

export const CLAIM_SIDES = ["customer", "supplier"] as const;
export type ClaimSide = (typeof CLAIM_SIDES)[number];

export const CLAIM_TYPES = [
  "RESERVE_CLAIM_RIGHT",
  "LIQUIDATED_DAMAGES",
  "EXPEDITED_FREIGHT",
  "CONSEQUENTIAL_LOSS",
  "REPLACEMENT_COST",
  "REPAIR_COST",
  "SURVEY_FEE",
  "RETURN_FREIGHT",
  "STORAGE_FEE",
  "SHORTAGE_VALUE",
  "REWORK_LOSS",
  "CUSTOMER_PENALTY",
  "FULL_REFUND",
  "PARTIAL_REFUND",
  "PRICE_DISCOUNT",
  "REPLACEMENT",
  "SUPPLEMENTARY_SHIPMENT",
  "GOODS_RETURN",
  "WARNING_NOTICE",
  "SUSPEND_ORDERS",
  "DEDUCT_RETURN_COST",
  "RECORD_ONLY",
  // Tám mục dưới đây có trong file "After Service Terms" nhưng thiếu ở danh sách 22.
  "PRODUCTION_DOWNTIME",
  "REPACK_COST",
  "REWORK",
  "RELABEL",
  "REVISED_DOCUMENT",
  "REISSUE_DOCUMENT",
  "REISSUE_CERTIFICATE",
  "CASE_BY_CASE",
] as const;
export type ClaimType = (typeof CLAIM_TYPES)[number];

/** Hình thức mang số tiền. Các hình thức còn lại là hành động (đổi hàng, cảnh cáo…), không có số tiền. */
export const MONETARY_CLAIM_TYPES: ReadonlySet<ClaimType> = new Set<ClaimType>([
  "LIQUIDATED_DAMAGES",
  "EXPEDITED_FREIGHT",
  "CONSEQUENTIAL_LOSS",
  "REPLACEMENT_COST",
  "REPAIR_COST",
  "SURVEY_FEE",
  "RETURN_FREIGHT",
  "STORAGE_FEE",
  "SHORTAGE_VALUE",
  "REWORK_LOSS",
  "CUSTOMER_PENALTY",
  "FULL_REFUND",
  "PARTIAL_REFUND",
  "PRICE_DISCOUNT",
  "DEDUCT_RETURN_COST",
  "PRODUCTION_DOWNTIME",
  "REPACK_COST",
  "CASE_BY_CASE",
]);

/** Chỉ có nghĩa khi Hisense đòi nhà cung cấp. */
export const SUPPLIER_ONLY_CLAIM_TYPES: ReadonlySet<ClaimType> = new Set<ClaimType>([
  "CUSTOMER_PENALTY",
  "WARNING_NOTICE",
  "SUSPEND_ORDERS",
]);

export const CLAIM_SEVERITIES = ["Critical", "Major", "Minor"] as const;
export type ClaimSeverity = (typeof CLAIM_SEVERITIES)[number];

export const ISSUE_CATEGORIES = [
  "DELIVERY_DELAY",
  "GOODS_DAMAGE",
  "NON_CONFORMING",
  "SHORTAGE_OVER",
  "PACKAGING_DEFECT",
  "QUALITY_DEFECT",
  "DOCUMENTATION_ERROR",
  "OTHERS",
] as const;
export type IssueCategory = (typeof ISSUE_CATEGORIES)[number];

export interface IssueDefinition {
  code: string;
  category: IssueCategory;
  /** `null` = khách ghi "Minor / Major": người lập hồ sơ chọn theo mức thực tế. */
  severity: ClaimSeverity | null;
  claimTypes: readonly ClaimType[];
}

const issue = (code: string, category: IssueCategory, severity: ClaimSeverity | null, claimTypes: ClaimType[]): IssueDefinition => ({
  code,
  category,
  severity,
  claimTypes,
});

/** Đúng thứ tự và nội dung file "After Service Terms for ERP" (25 dòng). */
export const ISSUE_TYPES: readonly IssueDefinition[] = [
  issue("LATE_SHIPMENT", "DELIVERY_DELAY", "Critical", ["LIQUIDATED_DAMAGES", "EXPEDITED_FREIGHT", "RESERVE_CLAIM_RIGHT", "WARNING_NOTICE"]),
  issue("LATE_ARRIVAL", "DELIVERY_DELAY", "Major", ["CONSEQUENTIAL_LOSS", "RESERVE_CLAIM_RIGHT", "WARNING_NOTICE"]),
  issue("MISSED_SCHEDULE", "DELIVERY_DELAY", "Critical", ["PRODUCTION_DOWNTIME", "SUSPEND_ORDERS", "WARNING_NOTICE"]),
  issue("PARTIAL_DAMAGE", "GOODS_DAMAGE", "Major", ["REPLACEMENT_COST", "REPAIR_COST", "SURVEY_FEE", "PARTIAL_REFUND"]),
  issue("TOTAL_DAMAGE", "GOODS_DAMAGE", "Critical", ["FULL_REFUND", "RETURN_FREIGHT", "STORAGE_FEE", "WARNING_NOTICE"]),
  issue("TRANSIT_DAMAGE", "GOODS_DAMAGE", "Major", ["REPLACEMENT_COST", "SURVEY_FEE", "RESERVE_CLAIM_RIGHT"]),
  issue("PACKAGING_DAMAGE", "GOODS_DAMAGE", "Major", ["REPAIR_COST", "REPLACEMENT_COST", "WARNING_NOTICE"]),
  issue("DIFFERENT_FROM_SAMPLE", "NON_CONFORMING", "Critical", ["PRICE_DISCOUNT", "REPLACEMENT", "PARTIAL_REFUND", "FULL_REFUND", "WARNING_NOTICE"]),
  issue("WRONG_SPECIFICATION", "NON_CONFORMING", "Critical", ["REPLACEMENT", "FULL_REFUND", "SUSPEND_ORDERS"]),
  issue("WRONG_MATERIAL", "NON_CONFORMING", "Critical", ["REPLACEMENT", "FULL_REFUND", "REWORK_LOSS"]),
  issue("WRONG_COLOUR", "NON_CONFORMING", "Major", ["REPLACEMENT", "PRICE_DISCOUNT", "PARTIAL_REFUND"]),
  issue("WRONG_SIZE", "NON_CONFORMING", "Major", ["REPLACEMENT", "PRICE_DISCOUNT", "PARTIAL_REFUND"]),
  issue("WRONG_ITEM", "NON_CONFORMING", "Critical", ["REPLACEMENT", "FULL_REFUND"]),
  issue("SHORTAGE", "SHORTAGE_OVER", "Major", ["SHORTAGE_VALUE", "SUPPLEMENTARY_SHIPMENT", "PARTIAL_REFUND"]),
  issue("OVER_SHIPMENT", "SHORTAGE_OVER", "Minor", ["GOODS_RETURN", "DEDUCT_RETURN_COST", "RECORD_ONLY"]),
  issue("BROKEN_CARTON", "PACKAGING_DEFECT", "Minor", ["REPACK_COST", "RESERVE_CLAIM_RIGHT"]),
  issue("MISSING_LABEL", "PACKAGING_DEFECT", "Minor", ["RELABEL", "RECORD_ONLY"]),
  issue("INCORRECT_MARKING", "PACKAGING_DEFECT", "Minor", ["REVISED_DOCUMENT", "RECORD_ONLY"]),
  issue("SURFACE_DEFECT", "QUALITY_DEFECT", null, ["REWORK", "PRICE_DISCOUNT", "PARTIAL_REFUND"]),
  issue("FUNCTIONAL_DEFECT", "QUALITY_DEFECT", "Critical", ["REPLACEMENT", "FULL_REFUND", "CUSTOMER_PENALTY"]),
  issue("NON_COMPLIANT", "QUALITY_DEFECT", "Critical", ["FULL_REFUND", "SUSPEND_ORDERS", "WARNING_NOTICE"]),
  issue("WRONG_INVOICE", "DOCUMENTATION_ERROR", "Minor", ["REISSUE_DOCUMENT", "RECORD_ONLY"]),
  issue("WRONG_PACKING_LIST", "DOCUMENTATION_ERROR", "Minor", ["REISSUE_DOCUMENT", "RECORD_ONLY"]),
  issue("WRONG_CERTIFICATE", "DOCUMENTATION_ERROR", "Major", ["REISSUE_CERTIFICATE", "RESERVE_CLAIM_RIGHT"]),
  issue("OTHER", "OTHERS", "Minor", ["CASE_BY_CASE", "RECORD_ONLY"]),
];

export const ISSUE_CODES = ISSUE_TYPES.map((i) => i.code) as [string, ...string[]];

export function issueDefinition(code: string | null | undefined): IssueDefinition | undefined {
  return code ? ISSUE_TYPES.find((i) => i.code === code) : undefined;
}

/**
 * Hình thức chọn được cho một loại lỗi ở một phía.
 *
 * Đúng các hình thức khách ghi cho loại lỗi đó, cộng "Ghi nhận, không đòi". Phía
 * khách bỏ ba hình thức chỉ Hisense mới đòi nhà cung cấp được.
 */
export function allowedClaimTypes(code: string, side: ClaimSide): ClaimType[] {
  const def = issueDefinition(code);
  if (!def) return [];
  const types = def.claimTypes.includes("RECORD_ONLY") ? [...def.claimTypes] : [...def.claimTypes, "RECORD_ONLY" as const];
  return side === "customer" ? types.filter((t) => !SUPPLIER_ONLY_CLAIM_TYPES.has(t)) : types;
}

/** Mức độ của hồ sơ: theo loại lỗi; chỉ loại "Minor / Major" mới lấy lựa chọn của người dùng. */
export function resolveSeverity(code: string, chosen: ClaimSeverity | null | undefined): ClaimSeverity | null {
  const def = issueDefinition(code);
  if (!def) return null;
  if (def.severity) return def.severity;
  return chosen === "Minor" || chosen === "Major" ? chosen : null;
}

export const ROOT_CAUSES = ["Undetermined", "Internal", "Supplier", "Transport", "Customer"] as const;
export type RootCause = (typeof ROOT_CAUSES)[number];

/** Một dòng yêu cầu: hình thức + số tiền (tuỳ chọn, chỉ với hình thức mang tiền). */
export const claimLineInputSchema = z.object({
  type: z.enum(CLAIM_TYPES),
  /** Chuỗi thập phân tối đa 2 số lẻ; rỗng/null = chưa có số tiền. */
  amount: z.string().trim().max(20).nullable().default(null),
});
export type ClaimLineInput = z.input<typeof claimLineInputSchema>;
export interface ClaimLine {
  type: ClaimType;
  amountMinor: number | null;
}

export const claimCurrencySchema = z
  .string()
  .trim()
  .regex(/^[A-Z]{3}$/)
  .nullable()
  .default(null);

/**
 * Kiểm một bộ yêu cầu cho một loại lỗi ở một phía, trả về dạng lưu.
 *
 * Thuần, không đụng DB: dùng chung cho zod ở biên API và cho bài kiểm. Trả mã lỗi
 * thay vì ném, để zod gắn đúng đường dẫn trường.
 */
export function normalizeClaims(
  code: string,
  side: ClaimSide,
  lines: readonly { type: ClaimType; amount: string | null }[],
  currency: string | null,
): { ok: true; claims: ClaimLine[] } | { ok: false; error: string } {
  if (!issueDefinition(code)) return { ok: false, error: "claim_issue" };
  const allowed = new Set(allowedClaimTypes(code, side));
  const seen = new Set<ClaimType>();
  const claims: ClaimLine[] = [];
  for (const line of lines) {
    if (!allowed.has(line.type)) return { ok: false, error: "claim_type_not_allowed" };
    if (seen.has(line.type)) return { ok: false, error: "claim_type_duplicate" };
    seen.add(line.type);
    const raw = line.amount?.trim() ?? "";
    if (!raw) {
      claims.push({ type: line.type, amountMinor: null });
      continue;
    }
    if (!MONETARY_CLAIM_TYPES.has(line.type)) return { ok: false, error: "claim_amount_not_monetary" };
    let minor: number;
    try {
      minor = amountToMinor(raw, 2);
    } catch {
      return { ok: false, error: "claim_amount" };
    }
    claims.push({ type: line.type, amountMinor: minor });
  }
  if (seen.has("RECORD_ONLY") && seen.size > 1) return { ok: false, error: "claim_record_only_exclusive" };
  if (claims.some((c) => (c.amountMinor ?? 0) > 0) && !currency) return { ok: false, error: "claim_currency" };
  return { ok: true, claims };
}

/** Tổng tiền yêu cầu (một loại tiền của hồ sơ). */
export function claimTotalMinor(claims: readonly ClaimLine[]): number {
  return claims.reduce((sum, c) => sum + (c.amountMinor ?? 0), 0);
}
